import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@structural-climbing/ui'

export interface ChildAccount {
  status: 'pending' | 'expired' | 'active'
  email: string
}

interface Props {
  childId: string
  firstName: string
  /** Null when the child has never been invited. */
  account: ChildAccount | null
  /**
   * Whether the parent has set the child's gender. A login is not offered
   * without it: competitions could not place the child in a category.
   */
  hasGender: boolean
  /** Rendered at the end of the button row: the family card's Remove. */
  extraAction?: React.ReactNode
  /** Called after anything that changes the account, so the list reloads. */
  onChanged: () => void
}

// Server refusal codes to the sentence a parent can act on.
const ERROR_KEYS: Record<string, string> = {
  INVALID: 'family.accountErrorInvalid',
  OWN_EMAIL: 'family.accountErrorOwnEmail',
  EMAIL_IN_USE: 'family.accountErrorInUse',
  TOO_SOON: 'family.accountErrorTooSoon',
  EMAIL: 'family.accountErrorEmail',
  GENDER_REQUIRED: 'family.genderRequired',
}

/**
 * A child's own login, managed by the parent.
 *
 * The parent types an email and the child gets an invite to a limited app:
 * routes, standings, progress and competitions, with no membership in sight.
 * Kept out of FamilySection so that file's own flows are not disturbed.
 */
export function ChildAccountControl({
  childId,
  firstName,
  account,
  hasGender,
  onChanged,
  extraAction,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [inviteOpen, setInviteOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function call(method: 'POST' | 'DELETE', body?: { email: string }): Promise<boolean> {
    setBusy(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/children/${childId}/invite`, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      })
      if (res.ok) return true
      const data = (await res.json().catch(() => ({}))) as { code?: string }
      setError(t(ERROR_KEYS[data.code ?? ''] ?? 'family.accountError'))
      return false
    } catch {
      setError(t('family.accountError'))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function send(address: string) {
    if (await call('POST', { email: address.trim() })) {
      setInviteOpen(false)
      setEmail('')
      onChanged()
    }
  }

  async function remove() {
    if (await call('DELETE')) {
      setRemoveOpen(false)
      onChanged()
    }
  }

  function openInvite() {
    setError(null)
    setEmail('')
    setInviteOpen(true)
  }

  return (
    <div className="flex flex-col gap-2.5 border-t border-[var(--gym-line)] pt-3">
      {account && (
        <p className="min-w-0 break-words text-sm text-foreground/55">
          {t(
            account.status === 'active'
              ? 'family.accountActive'
              : account.status === 'pending'
                ? 'family.accountPending'
                : 'family.accountExpired',
            { email: account.email },
          )}
        </p>
      )}
      {!account && !hasGender && (
        <p className="text-sm text-foreground/55">
          {t('family.accountNeedsGender', { name: firstName })}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {!account && hasGender && (
          <Button
            size="sm"
            variant="bordered"
            className="border-[var(--gym-line)] font-semibold"
            onClick={openInvite}
          >
            {t('family.accountInvite', { name: firstName })}
          </Button>
        )}
        {account && account.status !== 'active' && (
          <Button
            size="sm"
            variant="bordered"
            className="border-[var(--gym-line)] font-semibold"
            isDisabled={busy}
            onClick={() => void send(account.email)}
          >
            {t('family.accountResend')}
          </Button>
        )}
        {account && (
          <Button
            size="sm"
            variant="bordered"
            className="border-[var(--gym-line)] font-semibold"
            isDisabled={busy}
            onClick={() => {
              setError(null)
              // Cancelling an invite nobody has used needs no second thought;
              // taking away a login somebody is using does.
              if (account.status === 'active') setRemoveOpen(true)
              else void remove()
            }}
          >
            {t(account.status === 'active' ? 'family.accountRemove' : 'family.accountCancel')}
          </Button>
        )}
        {extraAction}
      </div>
      {error && !inviteOpen && !removeOpen && <p className="mt-2 text-xs text-danger">{error}</p>}

      <Modal
        isOpen={inviteOpen}
        onOpenChange={(open) => {
          if (!open) setInviteOpen(false)
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('family.accountInviteTitle', { name: firstName })}</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-foreground/60">
                    {t('family.accountInviteIntro', { name: firstName })}
                  </p>
                  <Input
                    type="email"
                    label={t('family.accountEmailLabel')}
                    description={t('family.accountEmailHint')}
                    value={email}
                    onValueChange={setEmail}
                    autoFocus
                  />
                  <p className="text-xs text-foreground/40">
                    {t('family.accountConsent', { name: firstName })}
                  </p>
                  {error && <p className="text-sm text-danger">{error}</p>}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onClick={() => setInviteOpen(false)} isDisabled={busy}>
                  {t('common.cancel')}
                </Button>
                <Button
                  color="primary"
                  onClick={() => void send(email)}
                  isLoading={busy}
                  isDisabled={!email.trim()}
                >
                  {t('family.accountSend')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={removeOpen}
        onOpenChange={(open) => {
          if (!open) setRemoveOpen(false)
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('family.accountRemoveTitle', { name: firstName })}</ModalHeader>
              <ModalBody>
                <p className="text-sm text-foreground/60">
                  {t('family.accountRemoveMessage', { name: firstName })}
                </p>
                {error && <p className="text-sm text-danger">{error}</p>}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onClick={() => setRemoveOpen(false)} isDisabled={busy}>
                  {t('common.cancel')}
                </Button>
                <Button color="danger" onClick={() => void remove()} isLoading={busy}>
                  {t('family.accountRemove')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
